<script setup lang="ts">
import { computed, onActivated, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import useBasicStore from '~/store/modules/basic'
import useEnterpriseImageStore from '~/store/modules/enterpriseImage'
import { useSiteConfig } from '~/config/useSiteConfig'
import ImageOptionSelect from './ImageOptionSelect.vue'
import EnterpriseImageResult from './EnterpriseImageResult.vue'
import HighResolutionDialog from './HighResolutionDialog.vue'
import ImageBillingTooltip from './ImageBillingTooltip.vue'

const { t, te } = useI18n()
const store = useEnterpriseImageStore()
const basic = useBasicStore()
const { isVipSite } = useSiteConfig()
const {
  status, progress, originName, sourceUrl, resultUrl, dimensions, isHighResolutionSource,
  sourceLang, targetLang, selected, options, ocrEnhance, configLoading, configReady,
  isBusy, canUpload, canStart, errorKey, blockedKey, remainingWords, pendingHighResolution, operateRecordId, billing,
} = storeToRefs(store)
const fileInput = ref<HTMLInputElement>()
const dragging = ref(false)
const previewFailed = ref(false)
let languagesRequested = false

function initialize() {
  store.initialize()
  if (!basic.languages.length && !languagesRequested) {
    languagesRequested = true
    void basic.getLanguages().catch(() => { languagesRequested = false })
  }
}
onMounted(initialize)
onActivated(initialize)

const targetOptions = computed(() => basic.languages.map(lang => ({ value: lang.code, label: lang.name })))
const sourceOptions = computed(() => [{ value: '', label: t('enterprise_image_auto_detect') }, ...targetOptions.value])
const controlsDisabled = computed(() => isBusy.value || !!pendingHighResolution.value || !configReady.value)
const supportEmail = computed(() => isVipSite.value ? 'support@doclingo.vip' : 'support@doclingo.ai')

function translated(key: string) { return te(key) ? t(key) : '' }
function optionItems(group: 'scene' | 'fidelity' | 'repaint') {
  return options.value[group].map(option => {
    const disabled = group === 'repaint' && option.semantic === 'high_resolution' && !isHighResolutionSource.value
    return {
      value: option.value,
      label: translated(option.wordsKey) || option.semantic,
      description: disabled ? t('image2_high_resolution_unavailable_tip') : group === 'fidelity' ? translated(`${option.wordsKey}_desc`) : '',
      disabled,
      // 提示词可以共用 C 端配置，但企业界面不展示个人会员相关标签。
      tags: option.tags.filter(key => !/vip|free|watermark/i.test(key)).map(translated).filter(Boolean),
    }
  })
}

const currentRepaint = computed(() => options.value.repaint.find(option => option.value === selected.value.repaint))
const repaintSummary = computed(() => currentRepaint.value ? translated(`${currentRepaint.value.wordsKey}_summary`) : '')
const repaintRows = computed(() => currentRepaint.value ? ['suitable', 'consumption', 'tip'].map(field => ({
  field, label: t(`image2_render_field_${field}`), value: translated(`${currentRepaint.value!.wordsKey}_${field}`),
})).filter(row => row.value) : [])
const hasDimensions = computed(() => dimensions.value.width > 0 && dimensions.value.height > 0)
const highResolutionSelected = computed(() => currentRepaint.value?.semantic === 'high_resolution')
const previewUrl = computed(() => status.value === 'success' ? resultUrl.value : sourceUrl.value)
const previewSubtitle = computed(() => status.value === 'success' ? t('image2_preview_done') : sourceUrl.value ? t('image2_preview_selected') : t('image2_preview_waiting'))
const loadingLabel = computed(() => t(status.value === 'uploading' ? 'image2_status_uploading' : 'image2_status_translating'))
const percent = computed(() => Math.max(0, Math.min(100, Math.round(progress.value))))
const indeterminate = computed(() => status.value === 'translating' && percent.value === 0)
const canReloadConfig = computed(() => ['bizImage_err_config', 'bizImage_no_prompts', 'bizImage_unconfigured'].includes(blockedKey.value))
watch(previewUrl, () => { previewFailed.value = false })
watch(canUpload, value => { if (!value) dragging.value = false })

function openFilePicker() { if (canUpload.value) fileInput.value?.click() }
function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (file && canUpload.value) void store.selectFile(file)
}
function onDrop(event: DragEvent) {
  dragging.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file && canUpload.value) void store.selectFile(file)
}
</script>

<template>
  <div class="enterprise-image-workbench" data-testid="enterprise-image-workbench">
    <section class="image-settings-card">
      <header class="image-panel-head">
        <div class="image-panel-title-row">
          <h1>{{ $t('image2_upload_title') }}</h1>
          <ImageBillingTooltip :billing="billing" :support-email="supportEmail" enterprise />
        </div>
        <p>{{ $t('image2_upload_desc') }}</p>
      </header>
      <input ref="fileInput" class="image-file-input" type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" :disabled="!canUpload" @change="onFileChange" />
      <button
        type="button"
        class="image-dropzone"
        :class="{ 'is-dragging': dragging, 'is-disabled': !canUpload }"
        :disabled="!canUpload"
        :aria-disabled="!canUpload"
        :aria-label="$t('image2_dropzone_title')"
        @click="openFilePicker"
        @dragover.prevent="dragging = canUpload"
        @dragleave.prevent="dragging = false"
        @drop.prevent="onDrop"
      >
        <img src="/assets/images/workbench/image-upload.svg" width="22" height="22" alt="" aria-hidden="true" />
        <span class="image-dropzone-title">{{ originName || $t('image2_dropzone_title') }}</span>
        <span class="image-dropzone-hint">{{ $t('image2_dropzone_hint') }}</span>
      </button>

      <div class="image-field-grid">
        <ImageOptionSelect v-model="sourceLang" :label="$t('enterprise_image_source_lang')" :options="sourceOptions" :disabled="controlsDisabled" searchable />
        <ImageOptionSelect v-model="targetLang" :label="$t('choose_translation_language')" :options="targetOptions" :disabled="controlsDisabled" searchable />
        <ImageOptionSelect v-model="selected.scene" :label="$t('image2_label_scene')" :options="optionItems('scene')" :disabled="controlsDisabled" />
        <ImageOptionSelect v-model="selected.fidelity" :label="$t('image2_label_fidelity')" :options="optionItems('fidelity')" :disabled="controlsDisabled" />
      </div>
      <ImageOptionSelect v-model="selected.repaint" :label="$t('image2_label_render')" :options="optionItems('repaint')" :disabled="controlsDisabled" />

      <div v-if="hasDimensions && (!isHighResolutionSource || !highResolutionSelected)" class="image-strategy-notice" :class="{ warning: isHighResolutionSource }" role="status">
        <span class="image-notice-icon" aria-hidden="true" />
        <span>{{ $t(isHighResolutionSource ? 'image2_high_resolution_strategy_warning' : 'image2_standard_resolution_notice') }}</span>
      </div>
      <section v-if="currentRepaint" class="image-strategy-card">
        <div v-if="hasDimensions && isHighResolutionSource && highResolutionSelected" class="image-strategy-notice enabled" role="status">
          <span class="image-notice-icon" aria-hidden="true" />
          <span>{{ $t('image2_high_resolution_enabled_notice', dimensions) }}</span>
        </div>
        <h2><span class="image-strategy-swatch" aria-hidden="true" />{{ translated(currentRepaint.wordsKey) || currentRepaint.semantic }}</h2>
        <p v-if="repaintSummary" class="image-strategy-summary">{{ repaintSummary }}</p>
        <p v-for="row in repaintRows" :key="row.field" class="image-strategy-detail"><span>{{ row.label }}: </span>{{ row.value }}</p>
      </section>

      <label class="image-ocr-card" :class="{ 'is-disabled': controlsDisabled }">
        <span class="image-ocr-heading"><input v-model="ocrEnhance" type="checkbox" :disabled="controlsDisabled" /><span>{{ $t('image2_enhance_ocr_title') }}</span></span>
        <span class="image-ocr-hint">{{ $t('image2_enhance_ocr_hint') }}</span>
      </label>
      <div v-if="blockedKey" class="image-config-notice" role="status" aria-live="polite">
        <span>{{ $t(blockedKey) }}</span>
        <button v-if="canReloadConfig" type="button" :disabled="configLoading || isBusy" @click="store.loadConfig()">{{ $t('bizImage_reload') }}</button>
      </div>
      <p v-if="errorKey && status !== 'fail'" class="image-inline-error" role="alert">{{ $t(errorKey) }}</p>
      <p v-if="configReady && !blockedKey" class="image-quota">{{ $t('bizImage_quota', { count: remainingWords.toLocaleString() }) }}</p>
      <button type="button" class="image-translate-button" :disabled="!canStart || status === 'fail'" @click="store.startTranslate()">
        {{ status === 'uploading' || status === 'translating' ? loadingLabel : status === 'success' ? $t('image2_preview_done') : $t('image2_btn_translate') }}
      </button>
    </section>

    <div class="image-preview-column">
      <section class="image-preview-card">
        <header class="image-panel-head">
          <h2>{{ $t('image2_preview_title') }}</h2>
          <p>{{ previewSubtitle }}</p>
        </header>
        <div class="image-preview-box" :class="{ 'is-complete': status === 'success' }" :aria-busy="isBusy">
          <img v-if="previewUrl && !previewFailed" :key="previewUrl" class="image-preview" :src="previewUrl" crossorigin="anonymous" :alt="$t(status === 'success' ? 'image2_result_trans' : 'image2_result_orig')" @error="previewFailed = true" />
          <p v-else-if="previewFailed" class="image-preview-error" role="status">{{ $t('bizImage_error_image') }}</p>
          <img v-else src="/assets/images/workbench/image-image.svg" width="32" height="32" alt="" aria-hidden="true" />
          <div v-if="status === 'uploading' || status === 'translating'" class="image-progress-overlay">
            <div class="image-progress-track" :class="{ indeterminate }" role="progressbar" :aria-label="loadingLabel" :aria-valuenow="indeterminate ? undefined : percent" aria-valuemin="0" aria-valuemax="100">
              <span :style="indeterminate ? {} : { width: `${percent}%` }" />
            </div>
            <p role="status">{{ loadingLabel }} <span v-if="!indeterminate">{{ percent }}%</span></p>
          </div>
          <div v-if="status === 'fail'" class="image-failure-overlay" role="alert">
            <span class="image-failure-icon" aria-hidden="true" />
            <p>{{ $t(errorKey || 'bizImage_err_translate') }}</p>
            <button type="button" :disabled="isBusy || !!blockedKey || errorKey === 'bizImage_err_submission'" @click="store.retry()">{{ $t('bizImage_retry') }}</button>
          </div>
        </div>
      </section>
      <EnterpriseImageResult v-if="status === 'success' && operateRecordId" :record-id="operateRecordId" @saved="store.refreshResult()" />
    </div>
    <HighResolutionDialog v-if="pendingHighResolution" :width="pendingHighResolution.width" :height="pendingHighResolution.height" @close="store.cancelPendingFile()" @choose-other="store.chooseHighResolution(false)" @confirm="store.chooseHighResolution(true, $event)" />
  </div>
</template>

<style scoped lang="scss">
.enterprise-image-workbench { display: grid; grid-template-columns: 380px minmax(0, 1fr); gap: 20px; align-items: start; width: 100%; color: #1f2329; }
.enterprise-image-workbench, .enterprise-image-workbench * { box-sizing: border-box; }
.image-settings-card, .image-preview-card { min-width: 0; padding: 22px; border: 1px solid #ebeef2; border-radius: 14px; background: #fff; box-shadow: 0 4px 14px #1f23290a; }
.image-settings-card { display: flex; flex-direction: column; gap: 14px; }
.image-panel-head { display: flex; flex-direction: column; gap: 6px; }
.image-panel-title-row { display: flex; align-items: center; gap: 6px; min-width: 0; }
.image-panel-head h1, .image-panel-head h2 { font-size: 17px; font-weight: 800; line-height: 25px; margin: 0; color: #1f2329; }
.image-panel-head p { font-size: 12.5px; line-height: 21px; margin: 0; color: #51565d; }
.image-file-input { display: none; }
.image-dropzone { width: 100%; height: 150px; flex-shrink: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 16px; border: 1px solid #d9dee5; border-radius: 10px; background: #f7f8fa; font-family: inherit; cursor: pointer; }
.image-dropzone.is-dragging, .image-dropzone:hover:not(.is-disabled) { border-color: #2b7de9; background: #f0f6ff; }
.image-dropzone.is-disabled { cursor: not-allowed; }
.image-dropzone-title { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13.5px; line-height: 21px; font-weight: 600; color: #1f2329; }
.image-dropzone-hint { color: #8f959e; font-size: 11px; line-height: 17px; }
.image-field-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 12px; }
.image-strategy-card { display: flex; flex-direction: column; gap: 10px; padding: 14px 16px; border-radius: 10px; background: #f5f6f8; }
.image-strategy-card h2 { display: flex; align-items: flex-start; gap: 7px; margin: 0; font-size: 13px; line-height: 20px; font-weight: 700; color: #1f2329; }
.image-strategy-swatch { width: 14px; height: 14px; flex: 0 0 14px; margin-top: 3px; border-radius: 3px; background: #e3e7ec; }
.image-strategy-card p { margin: 0; font-size: 12px; line-height: 20px; }
.image-strategy-summary { color: #51565d; }
.image-strategy-detail { color: #8f959e; }
.image-strategy-notice { display: flex; align-items: flex-start; gap: 6px; padding: 10px 12px; border: 1px solid #ddeafa; border-radius: 8px; background: #f0f6ff; color: #5c7ca2; font-size: 11.5px; line-height: 19px; }
.image-notice-icon { flex: 0 0 14px; width: 14px; height: 14px; margin-top: 2px; background: currentColor; mask: url('/assets/images/workbench/icons/info.svg') center / contain no-repeat; }
.image-strategy-notice.warning { color: #bd750f; background: #fff8eb; border-color: #f8ddb4; }
.image-strategy-notice.warning .image-notice-icon { mask-image: url('/assets/images/workbench/icons/alert-triangle.svg'); }
.image-strategy-notice.enabled { padding: 0; border: 0; background: none; color: #2b7de9; }
.image-ocr-card { display: flex; flex-direction: column; gap: 8px; padding: 14px 16px; border: 1px solid #e3e7ec; border-radius: 10px; cursor: pointer; }
.image-ocr-card.is-disabled { cursor: not-allowed; }
.image-ocr-heading { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; line-height: 20px; font-weight: 600; }
.image-ocr-heading input { appearance: none; position: relative; flex: 0 0 15px; width: 15px; height: 15px; margin: 2px 0 0; border: 1px solid #c3c9d1; border-radius: 3px; background: #fff; cursor: inherit; }
.image-ocr-heading input:checked { background: #2b7de9; border-color: #2b7de9; }
.image-ocr-heading input:checked::after { content: ''; position: absolute; left: 4px; top: 1px; width: 4px; height: 8px; border: solid #fff; border-width: 0 1.5px 1.5px 0; transform: rotate(45deg); }
.image-ocr-heading input:disabled { border-color: #d9dee5; background-color: #f7f8fa; }
.image-ocr-heading input:disabled:checked { background-color: #bdd6f8; }
.image-ocr-hint { font-size: 11.5px; line-height: 19px; color: #8f959e; }
.image-config-notice { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; padding: 10px 12px; background: #f5f8fc; border-radius: 8px; font-size: 12px; line-height: 20px; color: #697586; }
.image-config-notice button { border: 0; padding: 0; background: none; color: #2b7de9; font: inherit; cursor: pointer; }
.image-config-notice button:disabled { color: #a6adb7; cursor: not-allowed; }
.image-inline-error { margin: 0; color: #e45a61; font-size: 12px; line-height: 20px; }
.image-quota { margin: 0; color: #8f959e; font-size: 11.5px; line-height: 18px; }
.image-translate-button { width: 100%; min-height: 42px; padding: 8px 16px; border: 0; border-radius: 10px; background: #2b7de9; color: #fff; font: inherit; font-size: 14px; line-height: 22px; font-weight: 700; cursor: pointer; }
.image-translate-button:disabled { background: #dbeafe; cursor: not-allowed; }
.image-preview-column { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
.image-preview-card { display: flex; flex-direction: column; gap: 8px; }
.image-preview-box { height: 320px; position: relative; display: flex; align-items: center; justify-content: center; overflow: hidden; border: 1px solid #eef1f5; border-radius: 8px; background: #fafafb; }
.image-preview-box.is-complete { height: 400px; }
.image-preview { display: block; width: 100%; height: 100%; object-fit: contain; }
.image-preview-error { font-size: 12px; line-height: 20px; color: #8f959e; padding: 16px; text-align: center; }
.image-progress-overlay, .image-failure-overlay { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 12px; padding: 20px; background: rgba(255, 255, 255, .88); }
.image-progress-track { height: 10px; width: min(280px, 80%); background: #e8edf4; border-radius: 6px; overflow: hidden; }
.image-progress-track span { display: block; height: 100%; background: #2b7de9; border-radius: inherit; transition: width .2s ease; }
.image-progress-track.indeterminate span { width: 40%; animation: enterprise-image-progress 1.4s ease-in-out infinite; }
.image-progress-overlay p { margin: 0; color: #51565d; font-size: 13px; line-height: 21px; }
.image-progress-overlay p span { margin-inline-start: 4px; }
.image-failure-icon { width: 28px; height: 28px; background: #e56a68; mask: url('/assets/images/workbench/icons/alert-triangle.svg') center / contain no-repeat; }
.image-failure-overlay p { max-width: 340px; text-align: center; margin: 0; color: #51565d; font-size: 13px; line-height: 22px; }
.image-failure-overlay button { padding: 8px 20px; border: 1px solid #2b7de9; border-radius: 8px; background: #fff; color: #2b7de9; font: inherit; font-size: 13px; cursor: pointer; }
.image-failure-overlay button:disabled { color: #a6adb7; border-color: #d9dee5; cursor: not-allowed; }
@keyframes enterprise-image-progress { from { transform: translateX(-100%); } to { transform: translateX(350%); } }
@media (max-width: 1100px) { .enterprise-image-workbench { grid-template-columns: 340px minmax(0, 1fr); gap: 16px; } }
@media (max-width: 900px) { .enterprise-image-workbench { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 480px) { .image-settings-card, .image-preview-card { padding: 18px; } .image-field-grid { grid-template-columns: minmax(0, 1fr); } .image-preview-box.is-complete { height: 320px; } }
@media (prefers-reduced-motion: reduce) { .image-progress-track span { transition: none; } .image-progress-track.indeterminate span { animation-duration: 3s; } }
</style>
